/**
 * tooltip.js — Singleton custom tooltip replacing native title attributes.
 * Uses [data-tooltip] attribute. Optional [data-tooltip-shortcut] for key hints.
 * XSS-safe: builds DOM with textContent, never innerHTML.
 * Keyboard-accessible: focusin/focusout show tooltip for tabbing users.
 */
import { prefersReducedMotion } from './feedback.js';

let tooltipEl = null;
let showTimer = null;
let currentTrigger = null;
let initialized = false;
const DELAY = 300;

export function initTooltips() {
  if (initialized) return;
  initialized = true;

  tooltipEl = document.createElement('div');
  tooltipEl.className = 'pb-tooltip';
  tooltipEl.dataset.pbKit = 'tooltip';
  tooltipEl.setAttribute('role', 'tooltip');
  tooltipEl.id = 'pb-tooltip';
  tooltipEl.hidden = true;
  document.body.appendChild(tooltipEl);

  // Mouse: delegated hover on entire document
  document.addEventListener('mouseover', onMouseOver, true);
  document.addEventListener('mouseout', onMouseOut, true);

  // Keyboard: focusin/focusout so keyboard users see tooltips too
  document.addEventListener('focusin', onFocusIn, true);
  document.addEventListener('focusout', onFocusOut, true);

  // Dismiss on click, scroll (any scrollable ancestor), keydown
  document.addEventListener('click', hide, true);
  window.addEventListener('scroll', hide, { capture: true, passive: true });
  document.addEventListener('keydown', onKeyDown, true);
}

function onMouseOver(e) {
  // A finger produces mouseover too, on the synthesized pass after touchend —
  // so a tap used to arm a 300ms timer for a tooltip that then had nothing to
  // dismiss it, since no mouseout follows. The result was a 300ms artifact
  // hanging over the control the user just pressed.
  //
  // Deliberately NOT replaced with a long-press tooltip: that collides with the
  // 250ms reorder long-press and with the iOS callout, and would add a third
  // gesture vocabulary. Touch users get accessible names and, on the glass
  // island, visible labels. Keyboard focus below is untouched and still shows.
  if (coarsePointerActive()) return;
  const trigger = e.target.closest('[data-tooltip]');
  if (!trigger || trigger === currentTrigger) return;
  scheduleShow(trigger);
}

/**
 * True when the pointer currently in use is not a mouse.
 *
 * Read from <html data-pb-pointer>, which shared/viewport-service.js keeps on
 * the LIVE pointer kind — the type of the most recent capture-phase
 * pointerdown, not a media query. A hybrid laptop reports `hover: hover` with a
 * touchscreen attached, so `@media (hover: none)` would suppress tooltips for
 * its mouse as well; this follows whichever input is actually in use.
 */
function coarsePointerActive() {
  return document.documentElement?.getAttribute('data-pb-pointer') === 'coarse';
}

function onMouseOut(e) {
  const trigger = e.target.closest('[data-tooltip]');
  if (!trigger) return;
  if (trigger.contains(e.relatedTarget)) return;
  hide();
}

function onFocusIn(e) {
  const trigger = e.target.closest('[data-tooltip]');
  if (!trigger || trigger === currentTrigger) return;
  scheduleShow(trigger);
}

function onFocusOut(e) {
  const trigger = e.target.closest('[data-tooltip]');
  if (!trigger) return;
  if (trigger.contains(e.relatedTarget)) return;
  hide();
}

function onKeyDown(e) {
  if (e.key === 'Escape' || currentTrigger) hide();
}

function scheduleShow(trigger) {
  hide();
  currentTrigger = trigger;
  showTimer = setTimeout(() => show(trigger), DELAY);
}

function show(trigger) {
  if (!tooltipEl || trigger !== currentTrigger) return;

  // Build content safely — no innerHTML with user data
  tooltipEl.textContent = '';

  const text = trigger.getAttribute('data-tooltip') || '';
  if (!text) {
    hide();
    return;
  }
  tooltipEl.appendChild(document.createTextNode(text));

  const shortcut = trigger.getAttribute('data-tooltip-shortcut');
  if (shortcut) {
    const isMac = navigator.platform.includes('Mac');
    const key = isMac
      ? shortcut.replace('Ctrl', '⌘').replace('Shift', '⇧').replace('Alt', '⌥')
      : shortcut;
    const kbd = document.createElement('kbd');
    kbd.className = 'pb-tooltip-kbd';
    kbd.dataset.pbKitPart = 'pb-tooltip-kbd';
    kbd.textContent = key;
    tooltipEl.appendChild(document.createTextNode(' '));
    tooltipEl.appendChild(kbd);
  }

  // Position: prefer below, flip above if near viewport bottom
  tooltipEl.hidden = false;
  const rect = trigger.getBoundingClientRect();
  const tipRect = tooltipEl.getBoundingClientRect();
  const GAP = 6;

  let top = rect.bottom + GAP;
  if (top + tipRect.height > window.innerHeight - 8) {
    top = rect.top - tipRect.height - GAP;
  }
  let left = rect.left + (rect.width - tipRect.width) / 2;
  left = Math.max(8, Math.min(left, window.innerWidth - tipRect.width - 8));

  tooltipEl.style.top = `${top}px`;
  tooltipEl.style.left = `${left}px`;

  if (!prefersReducedMotion()) {
    tooltipEl.classList.add('pb-tooltip-show');
  } else {
    tooltipEl.style.opacity = '1';
  }

  trigger.setAttribute('aria-describedby', 'pb-tooltip');
}

function hide() {
  clearTimeout(showTimer);
  if (currentTrigger) {
    currentTrigger.removeAttribute('aria-describedby');
    currentTrigger = null;
  }
  if (tooltipEl) {
    tooltipEl.hidden = true;
    tooltipEl.classList.remove('pb-tooltip-show');
    tooltipEl.style.opacity = '';
  }
}
